/**
 * ProtectedAdminRoute — Phase 5 update
 *
 * Access matrix after Phase 5:
 *
 *   role === "wholesaler_admin"   → ✅ allowed into /admin (wholesaler panel)
 *   role === "platform_admin"     → 🔀 redirected to /platform-admin (Phase 7 will create this page)
 *                                    For now redirects to /dashboard with a clear message;
 *                                    Phase 7 will replace this with the real platform admin panel.
 *   unauthenticated / non-admin   → 🔀 redirected to / or /dashboard
 *
 * This guard prevents platform admins from accidentally using the wholesaler
 * admin panel, which would show them one tenant's data (determined by their
 * non-existent wholesalerId) and create a confusing/broken experience.
 *
 * isAdmin (from AuthContext) is true for role-based admins only. We use
 * userRole to differentiate platform admins specifically.
 */
import { Navigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import theme from "../theme";

export default function ProtectedAdminRoute({ children }) {
  const { user, isAdmin, loading, userRole } = useAuth();

  if (loading || isAdmin === null) {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        fontSize: 14,
        color: theme.colors.textMid,
        fontFamily: "'Inter', sans-serif",
      }}>
        Verifying access…
      </div>
    );
  }

  if (!user) return <Navigate to="/" />;

  // Phase 5: platform admins have their own panel (/platform-admin, Phase 7).
  // Do NOT allow them into the wholesaler admin panel — they have no wholesalerId
  // so the panel would be empty/broken, and they should not see any single
  // tenant's data through this interface.
  if (userRole === "platform_admin") {
    window.location.replace(process.env.REACT_APP_PLATFORM_ADMIN_URL || "https://admin.urbamart.in");
    return null;
  }

  // wholesaler_admin → allow.
  if (!isAdmin) return <Navigate to="/dashboard" />;

  return children;
}
